import { useEffect, useMemo, useRef, useState } from "react"
import { usePayment } from "../use-payment"
import { useFormStore } from "@/stores/use-form-store"
import { FeeCategory, TransactionGateway } from "@/lib/types"
import { logger } from "@/utils/logger"
import { PaymentGateways } from "@/components/gateways/payment-gateways"
import { Button } from "@/components/ui/button"
import { useHandlePayment } from "./hooks/use-handle-payment"
import { getPeriodType, getPlanType } from "../utils"
import {
	OrderStatus,
	PaymentStatus,
	TransactionStatus,
	type FIELD,
	type PAYMENT_VERIFIED_FIELDS,
	type PaymentGatewayDetails,
} from "@/types/form-types"
import { progressivePoll } from "./hooks/use-polling"
import { RazorpayGateway } from "./hooks/razorpay"
import { EasebuzzGateway } from "./hooks/easebuzz"
import { toast } from "sonner"
import useGateways from "@/components/gateways/use-gateways"
import { useGetPaymentMeta } from "./hooks/use-get-payment-meta"
import type { UseFormReturn } from "react-hook-form"
import { getPaymentTwoFieldAttributes } from "../index"
import { useVerifiedFields } from "@/hooks/use-verified-fields"

// interface SelfPaymentActionProps {}

function SelfPaymentActions({
	config,
	programTypeFieldId,
}: {
	form: UseFormReturn
	config: FIELD
	programTypeFieldId: string
}) {
	const {
		setValues,
		feeSummary,
		course_id,
		selectedPaymentPlan,
		activeSelfPlans,
		getValues,
		couponDiscount,
		periodNumber,
		// formState,
	} = usePayment()

	const { order_id, final_payable_amount, stage_check_in_time } = getValues()
	const {
		config: formConfig,
		applicationId,
		verifiedFields,
		isPreview,
		setVerifiedFields,
	} = useFormStore()

	const { useSetVerifiedFields } = useVerifiedFields()
	const { mutateAsync: setDisableFields } = useSetVerifiedFields(
		formConfig?.university_id || "",
		formConfig?.batch_id || "",
		applicationId || "",
	)

	const { programFieldId, specializationFieldId, nationalityFieldId } =
		getPaymentTwoFieldAttributes(config.attributes)

	const [showPaymentGateway, setShowPaymentGateway] = useState(false)
	const showPaymentGatewayRef = useRef(showPaymentGateway)

	useEffect(() => {
		showPaymentGatewayRef.current = showPaymentGateway
	}, [showPaymentGateway])

	const { useCreateOrder, useCreateTransaction, checkTransactionStatus } =
		useHandlePayment({
			mode: window.__DF_MODE,
			applicationID: applicationId || "",
			batchID: formConfig?.batch_id || "",
			courseID: course_id,
			partnerID: formConfig?.partner_id || "",
			stage_check_in_time: stage_check_in_time,
		})
	// Fetch payment meta data
	const { data: paymentMeta } = useGetPaymentMeta({
		universityId: formConfig?.university_id || "",
		batchId: formConfig?.batch_id || "",
		applicationId: applicationId || "",
	})
	const { mutateAsync: createTransaction } = useCreateTransaction()

	const { useGetPaymentGateways } = useGateways()

	const { data: availableGateways = [] } = useGetPaymentGateways({
		batchId: formConfig?.batch_id || "",
		partnerId: formConfig?.partner_id || "",
	})

	//use the availableGateways to map the payment gateways
	const paymentGateways: PaymentGatewayDetails[] = availableGateways || []

	const selectedOrderItems = useMemo(() => {
		if (!feeSummary) return []

		return feeSummary.flatMap(item => {
			if (item.isDiscountComponent) return []
			return item.price_details?.map(price => {
				return {
					fee_component_id: item.component_id,
					component_dimension_id: item.dimension_id,
					amount: price.final_price,
					currency: item.currency,
					period_type: getPeriodType(item.frequency),
					period_number: price.period_number,
				}
			})
		})
	}, [feeSummary])

	const adjustments = useMemo(() => {
		if (
			!couponDiscount ||
			couponDiscount.discount === 0 ||
			couponDiscount.payment_mode === "LOAN"
		)
			return []

		const fee = feeSummary.find(
			fee => fee.coupon_discount && fee.coupon_discount > 0,
		)
		if (!fee || !fee.coupon_discount || fee.coupon_discount === 0) return []

		return [
			// {
			// 	...applicatioDiscount,
			// 	amount: applicatioDiscount.discount,
			// },

			{
				amount: fee.coupon_discount,
				currency: fee.currency,
				type: "DISCOUNT",
				applied_by: couponDiscount.applied_by,
				applied_at: stage_check_in_time,
				description: `{${couponDiscount.coupon_id}}-coupon applied`,
			},
		]
	}, [couponDiscount])

	const { mutateAsync: createOrder } = useCreateOrder({
		feeGridRevisionID: feeSummary[0]?.revision_id,
		category: FeeCategory.TUITION,
		plan: getPlanType(
			activeSelfPlans.find(
				plan =>
					plan.component_id ===
					selectedPaymentPlan.replace(/^(self|loan)-/, ""),
			)?.frequency || "",
		),
		semester: periodNumber || 1,
		currency: feeSummary[0]?.currency || "INR",
		mode: "SELF",
		method: "ONLINE",
		items: selectedOrderItems,
		adjustments: adjustments,
	})

	//called when user clicks on pay now
	const handlePayment = async () => {
		// useHandleOrder(PaymentPlan.SEMESTER, feeSummary, FeeCategory.TUITION)
		await createOrder(undefined, {
			onSuccess: val => {
				if (val.payment_required) {
					if (
						val.order.status === OrderStatus.PENDING ||
						val.order.status === OrderStatus.CREATED
					) {
						setValues({
							payment_status:
								PaymentStatus.ORDER_CREATED_PENDING_PAYMENT,
							order_id: val.order.order_id,
						})
						setShowPaymentGateway(true)
					}
				} else {
					setValues({
						payment_required: false,
						payment_status: PaymentStatus.PAYMENT_SUCCESS,
						payment_success: true,
						order_id: val.order.order_id,
						transaction_id: "-",
						hideDownloadReceipt: true,
					})
				}
			},
			onError: () => {
				setValues({
					payment_status: PaymentStatus.SERVICE_UNAVAILABLE,
				})
			},
		})
	}

	// Callback when payment reaches final state
	// const handlePaymentComplete = (data: any) => {
	// 	const order_id = data?.order_id

	// 	// Update form values
	// 	setValues(
	// 		{
	// 			orderId: order_id,
	// 			amount: data?.amount,
	// 			transactionId: data?.transaction_id,
	// 		},
	// 		// { shouldValidate: true, shouldDirty: true },
	// 	)
	// }

	// const {
	// 	paymentStatus,
	// 	useHandleOrder,
	// 	useHandlePayment,
	// 	isLoading,
	// 	paymentInfo,
	// } = useRazorpay(
	// 	window.__DF_MODE,
	// 	formConfig!,
	// 	applicationId || "",
	// 	feeSummary,
	// 	FeeCategory.TUITION,
	// 	handlePaymentComplete,
	// )

	const tempFunction = async (payload: PAYMENT_VERIFIED_FIELDS[]) => {
		const data = await setDisableFields(payload, {
			onSuccess: () => {
				setVerifiedFields([
					{
						fieldId: programFieldId || "",
						isVerified: true,
					},
					{
						fieldId: specializationFieldId || "",
						isVerified: true,
					},
					{
						fieldId: nationalityFieldId || "",
						isVerified: true,
					},
					{
						fieldId: programTypeFieldId || "",
						isVerified: true,
					},
				])
			},
			onError: () => {
				logger.error("Verified Fields Not Set")
			},
		})

		if (data.status >= 200 && data.status < 300) {
			logger.info("Verified Fields Set")
			setVerifiedFields([
				{
					fieldId: programFieldId || "",
					isVerified: true,
				},
				{
					fieldId: specializationFieldId || "",
					isVerified: true,
				},
				{
					fieldId: nationalityFieldId || "",
					isVerified: true,
				},
				{
					fieldId: programTypeFieldId || "",
					isVerified: true,
				},
			])
		}
	}
	const handleGatewaySelect = async (gatewayId: string) => {
		logger.info("Gateway selected:", gatewayId)

		setShowPaymentGateway(false) // disable all immediately

		// Find the selected gateway to get its credentials
		const selectedGateway = paymentGateways.find(
			gateway => gateway.gateway === gatewayId,
		)

		// Extract Razorpay key if it's a Razorpay gateway
		const key = selectedGateway?.credentials?.key

		await createTransaction(
			{
				orderID: order_id,
				gatewayID: gatewayId,
				paymentMeta: paymentMeta!,
			},
			{
				onSuccess: async val => {
					setValues({
						transaction_id: val.transaction_id,
						payment_status: PaymentStatus.PAYMENT_IN_PROGRESS,
					})

					if (
						val.gateway === TransactionGateway.Razorpay &&
						applicationId
					) {
						await RazorpayGateway.initPayment({
							orderId: val.gateway_order_id || "",
							transactionId: val.transaction_id,
							amount: final_payable_amount,
							currency: val.currency,
							applicationId: applicationId,
							gatewayId: val.gateway,
							access_key: val.data?.access_key || "",
							setValues: setValues,
							mode: window.__DF_MODE,
							batchId: formConfig?.batch_id || "",
							partnerId: formConfig?.partner_id || "",
							modeEazebuzz: "",
							key: key || "",
							paymentMeta: paymentMeta,
							setShowPaymentGateway: setShowPaymentGateway,
							showPaymentGatewayRef: showPaymentGatewayRef,
						})
					} else if (
						val.gateway === TransactionGateway.Easebuzz &&
						applicationId
					) {
						await EasebuzzGateway.initPayment({
							orderId: order_id,
							transactionId: val.transaction_id,
							amount: final_payable_amount,
							currency: val.currency,
							applicationId: applicationId,
							gatewayId: val.gateway,
							access_key: val.data?.access_key || "",
							setValues: setValues,
							mode: window.__DF_MODE,
							batchId: formConfig?.batch_id || "",
							partnerId: formConfig?.partner_id || "",
							modeEazebuzz: val.data?.mode || "",
							key: "", //Easebuzz doesn't require a key
							setShowPaymentGateway: setShowPaymentGateway,
							showPaymentGatewayRef: showPaymentGatewayRef,
						})
					} else {
						toast.error("gateway not supported")
					}

					const result = await progressivePoll(
						async () => {
							const data = await checkTransactionStatus({
								orderID: order_id,
								transactionID: val.transaction_id,
								category: FeeCategory.TUITION,
								semester: periodNumber,
							})

							return data
						},
						{
							isDone: result => {
								if (!result) return false

								if (showPaymentGatewayRef.current) return true

								return ["SUCCESS", "FAILED"].includes(
									result?.status,
								)
							},
							getInterval: elapsedMs => {
								const elapsedMinutes = Math.floor(
									elapsedMs / 60000,
								)
								if (elapsedMinutes < 1) return 3000
								if (elapsedMinutes < 3) return 7000
								if (elapsedMinutes < 5) return 10000
								if (elapsedMinutes < 10) return 20000
								return 30000
							},
							// timeout: 10 * 60 * 1000, // 10 minutes
						},
					)

					if (result && result.status) {
						if (result.status === TransactionStatus.SUCCESS) {
							setValues({
								payment_success: true,
								payment_status: PaymentStatus.PAYMENT_SUCCESS,
								paidAt: result.paid_at,
							})
							if (
								programFieldId &&
								verifiedFields.has(programFieldId) &&
								specializationFieldId &&
								verifiedFields.has(specializationFieldId) &&
								nationalityFieldId &&
								verifiedFields.has(nationalityFieldId)
							) {
								return
							}

							const payload: PAYMENT_VERIFIED_FIELDS[] = [
								{
									field_id: programFieldId || "",
									is_verified: true,
								},
								{
									field_id: specializationFieldId || "",
									is_verified: true,
								},
								{
									field_id: nationalityFieldId || "",
									is_verified: true,
								},
								{
									field_id: programTypeFieldId || "",
									is_verified: true,
								},
							]
							tempFunction(payload)
						} else if (result.status === TransactionStatus.FAILED) {
							setValues({
								payment_status:
									PaymentStatus.PAYMENT_FAILED_RETRY_ALLOWED,
							})
						}
					}
				},
			},
		)
	}

	return showPaymentGateway ? (
		<PaymentGateways
			onSelect={gatewayId => handleGatewaySelect(gatewayId)}
			batchId={formConfig?.batch_id || ""}
			partnerId={formConfig?.partner_id || ""}
			showPaymentGateway={showPaymentGateway}
		/>
	) : (
		<Button
			onClick={handlePayment}
			type="button"
			className="mt-9 flex w-full items-center justify-center gap-2 rounded-2xl bg-green-600 text-white hover:bg-green-500"
			disabled={isPreview}
		>
			<svg
				width="20"
				viewBox="0 0 48 48"
				xmlns="http://www.w3.org/2000/svg"
			>
				<g
					fill="none"
					stroke="currentColor"
					strokeWidth="4"
					strokeLinejoin="round"
				>
					<path d="M4 10a2 2 0 0 1 2-2h36a2 2 0 0 1 2 2v28a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10z" />
					<path
						d="M4 16h40"
						strokeLinecap="square"
					/>
					<path
						d="M27 32h9"
						strokeLinecap="round"
					/>
					<path
						d="M44 10v16"
						strokeLinecap="round"
					/>
					<path
						d="M4 10v16"
						strokeLinecap="round"
					/>
				</g>
			</svg>
			<span>Pay Now</span>
		</Button>
	)
}

export default SelfPaymentActions
